import React from "react";
import { Box, Container, Grid, Typography, Paper, Button } from "@mui/material";
import { fontWeight, styled } from "@mui/system";
import { Link } from "react-router-dom";

// Styled components for images and videos
const CircularImage = styled("img")({
  width: "100%",
  height: "300px",
  borderRadius: "10px",
  objectFit: "cover",
});

const StyledVideo = styled("video")({
  width: "100%",
  height: "100%",
  borderRadius: "10px",
  objectFit: "cover",
});

// Keyframe animations
const slideInFromLeft = `
  @keyframes slideInFromLeft {
    0% {
      transform: translateX(-100%);
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const slideInFromRight = `
  @keyframes slideInFromRight {
    0% {
      transform: translateX(100%);
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const fadeIn = `
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

// Images and Videos
const images = [
  "https://www.theghanareport.com/wp-content/uploads/2021/08/Jobs-1.jpg",
  "https://stl.tech/wp-content/uploads/2023/02/Network-services-scaled.webp",
  "https://images.pexels.com/photos/2263436/pexels-photo-2263436.jpeg?cs=srgb&dl=pexels-teddy-2263436.jpg&fm=jpg",
  "https://static6.depositphotos.com/1014014/641/i/450/depositphotos_6416346-stock-photo-special-offers-3d-illustration-design.jpg",
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS1RQqEqzPAZPE4ewFfyUyDRXo0WZn4gvk96pn0f2Q913Rppkwm9xAJK1myVRlfeIPKTqo&usqp=CAU",
  "https://media.licdn.com/dms/image/v2/D5612AQFxCMLsG4GKWg/article-cover_image-shrink_720_1280/article-cover_image-shrink_720_1280/0/1715143946034?e=2147483647&v=beta&t=OC9FP1e2Xtgnylv-eoOoqj7FY-oXv-c5-35QOQ98wuE",
];

const videos = [
  "/videos/Jobs.mp4",
  "/videos/Services.mp4",
  "/videos/Events.mp4",
  "/videos/Offers.mp4",
  "/videos/Ads.mp4",
  "/videos/classifieds.mp4"
];

// Content sections
const sections = [
  {
    title: "Jobs",
    text: "Looking for a new opportunity.. Our Jobs section is your gateway to top career listings across multiple industries. Whether you're a fresh graduate, an experienced professional, or a company searching for talent, we've got you covered. Browse jobs by category, location, or keyword.Stay ahead of the curve by joining our job alert system. ",
  },
  {
    title: "Services",
    text: "Our Services section provides access to a full spectrum of professional support tailored to your business needs. From digital marketing and branding to IT solutions and customer service outsourcing, we help you streamline operations and boost growth. Whether you're a startup or an established business, we offer flexible packages to suit your goals.",
  },
  {
    title: "Events",
    text: "Stay connected and inspired with our Events section, where we bring professionals together throughout the year. Whether online or in person, these events are designed to foster collaboration and innovation. Meet experts, discover new tools, and expand your network. Our event calendar is updated regularly, so there's always something to look forward to. Be part of a community that thrives on shared learning.",
  },
  {
    title: "Offers",
    text: "Take advantage of the best deals with our constantly updated Offers section. Whether you're shopping for personal needs or running a business, we feature discounts, promo codes, and exclusive bundles you won't find elsewhere. From seasonal sales to partner promotions, we make saving money easy. We cover a wide range of categories, ensuring there's something for everyone. Great value is just a click away.",
  },
  {
    title: "Sponsored Ads",
    text: "Reach the right audience with our highly effective Sponsored Ads platform. Our system ensures that your business gets maximum exposure in front of interested customers. We use data-driven strategies to optimize ad placement, helping you achieve measurable results. Perfect for small businesses and large enterprises alike, our sponsored ads are designed to fit various budgets.",
  },
  {
    title: "Classifieds",
    text: "Our Classifieds section is the perfect place to buy, sell, or trade a wide range of items. From electronics and vehicles to home furniture, it's a one-stop shop for local and national listings. Posting is easy and free for most categories, giving individuals and businesses a platform to connect. Each listing includes detailed descriptions, photos, and contact information.",
  },
];

// Paper styling
const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: "50px",
  backgroundColor: "rgb(247, 245, 253)",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  transition: "transform 0.3s ease-out",
  ":hover": { transform: "scale(1.05)" },
  "&.slideInLeft": {
    animation: "slideInFromLeft 1s ease-out",
  },
  "&.slideInRight": {
    animation: "slideInFromRight 1s ease-out",
  },
  "& .fadeInText": {
    animation: "fadeIn 1.5s ease-out",
  },
}));

const AboutUs = () => {
  return (
    <Container sx={{marginTop:"20px"}}>
      {/* Keyframe Styles */}
      <style>
        {`
          ${slideInFromLeft}
          ${slideInFromRight}
          ${fadeIn}
        `}
      </style>

      {/* Dynamic Sections */}
      {sections.map((section, index) => (
        <Box key={section.title} sx={{ marginBottom: "40px" }}>
          <Grid container spacing={4} alignItems="center">
            {index % 2 === 0 ? (
              <>
                <Grid item xs={12} md={6}>
                  {videos[index] ? (
                    <StyledVideo src={videos[index]} controls muted />
                  ) : (
                    <CircularImage src={images[index]} alt={section.title} />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <SectionPaper className="slideInLeft">
                    <Typography variant="h4" sx={{ marginBottom: "10px", color:"#42326e", fontWeight:"bold" }}>
                      {section.title}
                    </Typography>
                    <Typography variant="body1" className="fadeInText">
                      {section.text}
                    </Typography>
                  </SectionPaper>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} md={6}>
                  <SectionPaper className="slideInRight">
                    <Typography variant="h4" sx={{ marginBottom: "10px", color:"#42326e",fontWeight:"bold" }}>
                      {section.title}
                    </Typography>
                    <Typography variant="body1" className="fadeInText">
                      {section.text}
                    </Typography>
                  </SectionPaper>
                </Grid>
                <Grid item xs={12} md={6}>
                  {videos[index] ? (
                    <StyledVideo src={videos[index]} controls muted />
                  ) : (
                    <CircularImage src={images[index]} alt={section.title} />
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      ))}

      {/* Newsletter CTA */}
      <Box sx={{ textAlign: "center", marginBottom: "40px" }}>
        <Typography
          variant="body1"
          sx={{ fontSize: "2rem", marginBottom: "20px", color: "black" }}
        >
          Join us for more updates and notifications...!!!
        </Typography>
        <Button
          variant="contained"
          size="medium"
          color="secondary"
          component={Link}
          to="/newsletter"
          sx={{
            px: 4,
            py: 1,
            fontSize: "1.2rem",
            borderRadius: "10px",
            textAlign: "center",
            backgroundColor: "#42326e",
            color: "White",
            "&:hover": {
              backgroundColor: "black",
              color: "white",
            },
          }}
        >
          Join for Newsletter
        </Button>
      </Box>
    </Container>
  );
};

export default AboutUs;
